import {
  Button,
  FileInputControlled,
  Modal,
  TextAreaInput,
} from '@octano/global-ui';
import { Children, cloneElement, useEffect } from 'react';
import { Control, DeepMap, FieldError, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';

export type IInput = {
  [key: string]: File;
};

type IUploadModalProps = {
  isOpen: boolean;
  prefix?: string;
  title?: string;
  subtitle?: string;
  children: JSX.Element[] | JSX.Element;
  onClose: () => void;
  submitFunction?: (values: IInput) => void;
  onSuccess?: () => void;
  onError?: (errors: DeepMap<IInput, FieldError>) => void;
};

export default function UploadModal({
  isOpen,
  prefix,
  title = 'title',
  subtitle = 'subtitle',
  children,
  onClose,
  submitFunction = (values: IInput) => console.log(values),
  onSuccess,
  onError,
}: IUploadModalProps) {
  const { t } = useTranslation();
  const methods = useForm<IInput>({ mode: 'onSubmit' });
  const {
    reset,
    formState: { isSubmitting, isSubmitSuccessful },
    handleSubmit,
    control,
  } = methods;

  useEffect(() => reset(), [isOpen, reset]);
  useEffect(() => {
    isSubmitSuccessful && onSuccess && onSuccess();
  }, [isSubmitSuccessful, onSuccess]);

  const modalTitle =
    (prefix && t(`${prefix}.modalTitle`, { defaultValue: null })) || title;
  const modalSubtitle =
    (prefix && t(`${prefix}.modalSubtitle`, { defaultValue: null })) ||
    subtitle;
  return (
    <Modal isOpen={isOpen} toggle={onClose} closeOnBackdrop={true}>
      <Row className="text-center">
        <Col xs={12} className="pb-3">
          <span className="fs-22 text-dark">{modalTitle}</span>
        </Col>
        <Col xs={12}>
          <p className="fs-16 fw-300">{modalSubtitle}</p>
        </Col>
      </Row>
      <Form onSubmit={handleSubmit(submitFunction, onError)}>
        <Row className="mb-5">
          {Children.map(children, (child) => {
            if (child.type === UploadInput || child.type === UploadTextArea) {
              return cloneElement(child, {
                control,
                isSubmitting,
              });
            }
            return null;
          })}
        </Row>
        <Row>
          <Col xs={12} sm={6} className="pb-2">
            <Button
              onClick={onClose}
              text={t('tuitionContinuity.signDocumentation.buttons.close')}
              outlined
              fullwidth
              disabled={isSubmitting}
            />
          </Col>
          <Col xs={12} sm={6} className="pb-2">
            <Button
              type="submit"
              text={t('tuitionContinuity.signDocumentation.buttons.save')}
              fullwidth
              loading={isSubmitting}
            />
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

interface IUploadInputProps {
  control?: Control<IInput>;
  isSubmitting?: boolean;
  prefix?: string;
  name: string;
  label: string;
  accept?: string;
  btnText?: string;
  requiredMsgError: string;
}

interface IUploadTextAreaProps extends IUploadInputProps {
  height?: number;
}

function UploadInput({
  name,
  label,
  control,
  isSubmitting,
  accept = 'image/png, image/jpeg, application/pdf',
  btnText,
  requiredMsgError,
}: IUploadInputProps) {
  return (
    <Col xs={12}>
      <FileInputControlled
        name={name}
        label={label}
        rules={{
          required: requiredMsgError,
        }}
        accept={accept}
        control={control}
        disabled={isSubmitting}
        btnText={btnText}
      />
    </Col>
  );
}

interface IUploadTextAreaProps extends IUploadInputProps {
  height?: number;
}

function UploadTextArea({
  name,
  label,
  control,
  requiredMsgError,
  height = 200,
}: IUploadTextAreaProps) {
  return (
    <Col xs={12}>
      <TextAreaInput
        name={name}
        label={label}
        control={control}
        height={height}
        rules={{
          required: requiredMsgError,
        }}
      />
    </Col>
  );
}

UploadModal.TextArea = UploadTextArea;
UploadModal.Input = UploadInput;
